import type { MessageIndex } from "@/scripts/build-message-index"

export type Provenance =
  | { status: "exact"; key: string }
  | { status: "ambiguous"; candidates: string[] }
  | { status: "miss" }

export type CmsProvenance = {
  status: "cms"
  /**
   * How the field seeds/applies: "lexical" rich-text (edited whole-body in a
   * modal) vs a non-lexical "scalar"/"html" field. Read from the `data-cms-kind`
   * stamp the injector writes on the region element.
   */
  kind: "scalar" | "html" | "lexical"
  collection: string
  id: string
  field: string
  locale: string
}

function parseKind(v: string | undefined): CmsProvenance["kind"] {
  if (v === "scalar" || v === "html" || v === "lexical") return v
  return "scalar"
}

/**
 * Read CMS provenance from the nearest `data-cms-*` ancestor (injected
 * client-side by the edit-region machinery, never in public HTML), or null when
 * the clicked element isn't inside a CMS surface. An exact, unambiguous hit — no
 * value→key guessing like the next-intl path. `data-cms-kind` carries the field
 * shape (scalar / html / lexical).
 */
export function resolveCmsProvenance(el: HTMLElement): CmsProvenance | null {
  const host = el.closest<HTMLElement>("[data-cms-collection]")
  if (!host) return null
  const d = host.dataset
  if (!d.cmsCollection || !d.cmsId || !d.cmsField) return null
  return {
    status: "cms",
    kind: parseKind(d.cmsKind),
    collection: d.cmsCollection,
    id: d.cmsId,
    field: d.cmsField,
    locale: d.cmsLocale ?? "en",
  }
}

/**
 * Read the exact next-intl key from the nearest `data-i18n-key` ancestor — the
 * render-time stamp injected client-side on edit-mode activation (never in
 * public HTML), the i18n analogue of `resolveCmsProvenance`. When present it
 * resolves a click exactly, so the value→key guess + "on this page"
 * disambiguation radio below is only reached for un-stamped nodes. (AHD-7514)
 */
export function resolveI18nKeyStamp(el: HTMLElement): string | null {
  const host = el.closest<HTMLElement>("[data-i18n-key]")
  return host?.getAttribute("data-i18n-key") ?? null
}

/** Rank candidate keys so any whose namespace appears in the page path sorts first. */
function rankByPage(candidates: string[], pagePath: string): string[] {
  const tokens = pagePath.split("/").filter(Boolean)
  return [...candidates].sort((a, b) => {
    const aHit = tokens.some((t) => a.startsWith(`${t}.`)) ? 0 : 1
    const bHit = tokens.some((t) => b.startsWith(`${t}.`)) ? 0 : 1
    return aHit - bHit
  })
}

/** Map a visible string + current page to its next-intl key(s). */
export function resolveProvenance(
  text: string,
  pagePath: string,
  index: MessageIndex
): Provenance {
  const hits = index.byValue[text.trim()]
  if (!hits || hits.length === 0) return { status: "miss" }
  const keys = Array.from(new Set(hits.map((h) => h.key)))
  if (keys.length === 1) return { status: "exact", key: keys[0] }
  return { status: "ambiguous", candidates: rankByPage(keys, pagePath) }
}
